import { createContext, useContext, ReactNode, useMemo } from 'react';
import type { CRUDPermission } from '../../api/permissionsApi';

interface PermissionContextValue {
  parentPath: string | null;
  inheritedPermissions: CRUDPermission[];
  inheritedFrom: string | null;
}

const PermissionContext = createContext<PermissionContextValue>({
  parentPath: null,
  inheritedPermissions: [],
  inheritedFrom: null,
});

interface PermissionProviderProps {
  path: string;
  permissions: CRUDPermission[];
  children: ReactNode;
}

export function PermissionProvider({ path, permissions, children }: PermissionProviderProps) {
  const parent = useContext(PermissionContext);
  
  const value = useMemo(() => ({
    parentPath: path,
    inheritedPermissions: permissions.length > 0 ? permissions : parent.inheritedPermissions,
    inheritedFrom: permissions.length > 0 ? path : parent.inheritedFrom,
  }), [path, permissions, parent.inheritedPermissions, parent.inheritedFrom]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissionContext() {
  return useContext(PermissionContext);
}

export function useInheritedPath(localPath?: string): string {
  const { parentPath } = usePermissionContext();
  
  if (!localPath) return parentPath || '';
  if (!parentPath) return localPath;
  
  // Auto-generate hierarchical path
  return `${parentPath}.${localPath}`;
}
